import React from 'react';
import { Clock, Zap, FlaskConical } from 'lucide-react';
import type { MealPlanTotalsSummary } from '../../services/nutrition/mealPlanService';

export type MealPlanOverviewCardsProps = {
  summary: MealPlanTotalsSummary | null;
  marginTop?: number;
  emptyMessage?: string;
};

export const MealPlanOverviewCards: React.FC<MealPlanOverviewCardsProps> = ({
  summary,
  marginTop = 16,
  emptyMessage = 'Overview data is not available.',
}) => {
  const actuals = summary && (Object.keys(summary.totals || {}).length > 0)
    ? summary.totals!
    : (summary?.meal_plan_totals || {}) as Record<string, number>;
  const targets = summary?.meal_plan_doctor_modified_totals || summary?.meal_plan_base_icmr_totals || {};
  const fmt = (v: unknown, decimals = 1) =>
    v === null || v === undefined || Number.isNaN(Number(v)) ? '-' : Number(v).toFixed(decimals);
  const getStatus = (actual: number, target: number | null): string => {
    if (target === null || target === undefined || target === 0) return '—';
    const pct = (actual / target) * 100;
    if (pct >= 95 && pct <= 110) return 'OPTIMAL';
    if (pct > 110) return 'ABOVE TARGET';
    return 'BELOW TARGET';
  };

  const hasData = summary && (
    Object.keys(actuals || {}).length > 0 ||
    Object.keys(summary.meal_plan_totals || {}).length > 0
  );

  if (!hasData) {
    return (
      <div className="np-subcard" style={{ marginTop, padding: 24, textAlign: 'center', color: '#6b7280', fontSize: '0.95rem' }}>
        {emptyMessage}
      </div>
    );
  }

  const kcal = Number(actuals.calories) || 0;
  const protein = Number(actuals.protein_g) || 0;
  const carbs = Number(actuals.carbs_g) || 0;
  const fat = Number(actuals.fat_g) || 0;
  const fiber = Number(actuals.fiber_g) || 0;
  const totalMacro = protein + carbs + fat + fiber;
  const macroPcts = totalMacro > 0
    ? { protein: (protein / totalMacro) * 100, carbs: (carbs / totalMacro) * 100, fat: (fat / totalMacro) * 100, fiber: (fiber / totalMacro) * 100 }
    : { protein: 25, carbs: 25, fat: 25, fiber: 25 };

  const vitaminKeys: { key: string; label: string; unit: string }[] = [
    { key: 'vitamin_a_mcg', label: 'VITAMIN A', unit: 'mcg' },
    { key: 'vitamin_b1_mg', label: 'VITAMIN B1 (THIAMINE)', unit: 'mg' },
    { key: 'vitamin_b2_mg', label: 'VITAMIN B2 (RIBOFLAVIN)', unit: 'mg' },
    { key: 'vitamin_b3_mg', label: 'VITAMIN B3 (NIACIN)', unit: 'mg' },
    { key: 'vitamin_b6_mg', label: 'VITAMIN B6', unit: 'mg' },
    { key: 'vitamin_b12_mcg', label: 'VITAMIN B12', unit: 'mcg' },
    { key: 'vitamin_c_mg', label: 'VITAMIN C', unit: 'mg' },
    { key: 'vitamin_d_mcg', label: 'VITAMIN D', unit: 'mcg' },
    { key: 'vitamin_e_mg', label: 'VITAMIN E', unit: 'mg' },
    { key: 'vitamin_k_mcg', label: 'VITAMIN K', unit: 'mcg' },
    { key: 'folate_mcg', label: 'FOLATE (B9)', unit: 'mcg' },
  ];
  const mineralKeys: { key: string; label: string; unit: string }[] = [
    { key: 'calcium_mg', label: 'CALCIUM', unit: 'mg' },
    { key: 'iron_mg', label: 'IRON', unit: 'mg' },
    { key: 'magnesium_mg', label: 'MAGNESIUM', unit: 'mg' },
    { key: 'phosphorus_mg', label: 'PHOSPHORUS', unit: 'mg' },
    { key: 'potassium_mg', label: 'POTASSIUM', unit: 'mg' },
    { key: 'sodium_mg', label: 'SODIUM', unit: 'mg' },
    { key: 'zinc_mg', label: 'ZINC', unit: 'mg' },
    { key: 'copper_mg', label: 'COPPER', unit: 'mg' },
    { key: 'manganese_mg', label: 'MANGANESE', unit: 'mg' },
    { key: 'selenium_mcg', label: 'SELENIUM', unit: 'mcg' },
  ];
  const barColors = ['#2563eb', '#8b5cf6', '#ec4899', '#ef4444', '#f97316', '#84cc16', '#06b6d4'];
  const macroSlices = [
    { key: 'protein', label: 'PROTEIN', value: protein, pct: macroPcts.protein, color: '#1e3a5f' },
    { key: 'carbs', label: 'CARBS', value: carbs, pct: macroPcts.carbs, color: '#93c5fd' },
    { key: 'fat', label: 'FATS', value: fat, pct: macroPcts.fat, color: '#8b5cf6' },
    { key: 'fiber', label: 'FIBER', value: fiber, pct: macroPcts.fiber, color: '#22c55e' },
  ];
  const macroCallouts = totalMacro > 0
    ? (() => {
        let cumulativePct = 0;
        const anchorRatioByKey: Record<string, number> = {
          // Carbs occupies a large arc; anchor earlier so connector exits on the right, not bottom.
          carbs: 0.18,
        };
        return macroSlices.map((slice) => {
          const anchorRatio = anchorRatioByKey[slice.key] ?? 0.5;
          const midPct = cumulativePct + (slice.pct * anchorRatio);
          cumulativePct += slice.pct;
          const angle = ((midPct * 3.6) - 90) * (Math.PI / 180);
          // Angled callouts: radial out -> angled elbow -> short horizontal label tail.
          // Donut outer edge is ~51 (r=42 with strokeWidth=18).
          const x1 = 50 + Math.cos(angle) * 52;
          const y1 = 50 + Math.sin(angle) * 52;
          const x2 = 50 + Math.cos(angle) * 63;
          const y2 = 50 + Math.sin(angle) * 63;
          const x3 = 50 + Math.cos(angle) * 76;
          const y3 = 50 + Math.sin(angle) * 76;
          const isRight = Math.cos(angle) >= 0;
          const x4 = x3 + (isRight ? 14 : -14);
          return {
            ...slice,
            x1, y1, x2, y2, x3, y3, x4,
            isRight,
            valueLabel: `${fmt(slice.value, 0)}g`,
          };
        });
      })()
    : [];

  return (
    <div style={{ marginTop, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
      {/* Macronutrients card – donut */}
      <div className="np-subcard" style={{ padding: 20, position: 'relative' }}>
        <Clock size={18} style={{ position: 'absolute', top: 16, right: 16, color: '#9ca3af' }} />
        <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 16 }}>
          MACRONUTRIENTS
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: 16, marginTop: 12, minHeight: 280 }}>
          <div style={{ overflow: 'visible', padding: 60, boxSizing: 'content-box' }}>
            <div style={{ position: 'relative', width: 190, height: 190 }}>
              <svg viewBox="-6 -6 112 112" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%', display: 'block' }}>
                <circle cx="50" cy="50" r="42" fill="none" stroke="#e5e7eb" strokeWidth="18" />
                {totalMacro > 0 && (
                  <>
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#1e3a5f" strokeWidth="18" strokeDasharray={`${macroPcts.protein * 2.64} 264`} strokeDashoffset="0" />
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#93c5fd" strokeWidth="18" strokeDasharray={`${macroPcts.carbs * 2.64} 264`} strokeDashoffset={`${-macroPcts.protein * 2.64}`} />
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#8b5cf6" strokeWidth="18" strokeDasharray={`${macroPcts.fat * 2.64} 264`} strokeDashoffset={`${-(macroPcts.protein + macroPcts.carbs) * 2.64}`} />
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#22c55e" strokeWidth="18" strokeDasharray={`${macroPcts.fiber * 2.64} 264`} strokeDashoffset={`${-(macroPcts.protein + macroPcts.carbs + macroPcts.fat) * 2.64}`} />
                  </>
                )}
              </svg>
              {macroCallouts.length > 0 && (
                <svg
                  viewBox="0 0 100 100"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}
                >
                  {macroCallouts.map((c) => (
                    <g key={c.key}>
                      <line x1={c.x1} y1={c.y1} x2={c.x2} y2={c.y2} stroke={c.color} strokeWidth={2} strokeOpacity={1} strokeLinecap="round" />
                      <line x1={c.x2} y1={c.y2} x2={c.x3} y2={c.y3} stroke={c.color} strokeWidth={2} strokeOpacity={1} strokeLinecap="round" />
                      <line x1={c.x3} y1={c.y3} x2={c.x4} y2={c.y3} stroke={c.color} strokeWidth={2} strokeOpacity={1} strokeLinecap="round" />
                      <text
                        x={c.isRight ? c.x4 + 4 : c.x4 - 4}
                        y={c.y3 + 2}
                        textAnchor={c.isRight ? 'start' : 'end'}
                        fontSize="7.2"
                        fontWeight="700"
                        fill="#111827"
                        stroke="#ffffff"
                        strokeWidth="2.2"
                        paintOrder="stroke"
                      >
                        {c.valueLabel}
                      </text>
                    </g>
                  ))}
                </svg>
              )}
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>{kcal >= 1000 ? `${(kcal / 1000).toFixed(1)}k` : kcal} KCAL</span>
                <span style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 2 }}>TOTAL</span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: '#1e3a5f' }} /><span style={{ fontSize: '0.8rem', color: '#374151' }}>PROTEIN</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: '#93c5fd' }} /><span style={{ fontSize: '0.8rem', color: '#374151' }}>CARBS</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: '#8b5cf6' }} /><span style={{ fontSize: '0.8rem', color: '#374151' }}>FATS</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: '#22c55e' }} /><span style={{ fontSize: '0.8rem', color: '#374151' }}>FIBER</span></div>
          </div>
        </div>
      </div>

      {/* Key Vitamins card */}
      <div className="np-subcard" style={{ padding: 20, position: 'relative' }}>
        <Zap size={18} style={{ position: 'absolute', top: 16, right: 16, color: '#9ca3af' }} />
        <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 16 }}>
          KEY VITAMINS
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {vitaminKeys.map(({ key, label, unit }, idx) => {
            const current = Number(actuals[key]) || 0;
            const targetVal = Number(targets[key]) || 0;
            const target = targetVal > 0 ? targetVal : (Number((summary?.meal_plan_base_icmr_totals as Record<string, number>)?.[key]) || 0);
            const pct = target > 0 ? Math.min(100, (current / target) * 100) : 0;
            const status = getStatus(current, target > 0 ? target : null);
            const barColor = barColors[idx % barColors.length];
            return (
              <div key={key}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 4 }}>{fmt(current, key.includes('mcg') ? 1 : 2)}{unit} / {fmt(target, key.includes('mcg') ? 1 : 2)}{unit}</div>
                <div style={{ height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', backgroundColor: barColor, borderRadius: 4 }} />
                </div>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: status === 'OPTIMAL' ? '#10b981' : status === 'ABOVE TARGET' ? '#2563eb' : '#6b7280', marginTop: 2 }}>{status}</div>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e5e7eb', fontSize: '0.7rem', color: '#6b7280' }}>
          Vitamins and minerals show Doctor adjusted / ICMR Base.
        </div>
      </div>

      {/* Daily Minerals card */}
      <div className="np-subcard" style={{ padding: 20, position: 'relative' }}>
        <FlaskConical size={18} style={{ position: 'absolute', top: 16, right: 16, color: '#9ca3af' }} />
        <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 16 }}>
          DAILY MINERALS
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {mineralKeys.map(({ key, label, unit }, idx) => {
            const current = Number(actuals[key]) || 0;
            const targetVal = Number(targets[key]) || 0;
            const target = targetVal > 0 ? targetVal : (Number((summary?.meal_plan_base_icmr_totals as Record<string, number>)?.[key]) || 0);
            const pct = target > 0 ? Math.min(100, (current / target) * 100) : 0;
            const status = getStatus(current, target > 0 ? target : null);
            const barColor = barColors[idx % barColors.length];
            return (
              <div key={key}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 4 }}>{fmt(current, 1)}{unit} / {fmt(target, 1)}{unit}</div>
                <div style={{ height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', backgroundColor: barColor, borderRadius: 4 }} />
                </div>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: status === 'OPTIMAL' ? '#10b981' : status === 'ABOVE TARGET' ? '#2563eb' : '#6b7280', marginTop: 2 }}>{status}</div>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e5e7eb', fontSize: '0.7rem', color: '#6b7280' }}>
          Vitamins and minerals show Doctor adjusted / ICMR Base.
        </div>
      </div>
    </div>
  );
};
